// Приготвените рецепти на листа „Инвентаризация" — състоянието на секцията
// „Приготвени рецепти", съставките им и частта на всеки продукт в тях
// (`inRecipesByProduct`, от нея решетката смята намереното). Чистата сметка е в
// stocktakeRecipes.ts.
//
// Съставките на НОВА рецепта се теглят по текущата рецепта (`GET
// /recipe-ingredients`, ключ `["inventory","recipe-ingredients",id]`); вече
// записаната в документа (редакция) ползва снимката си — и когато я махнеш и
// добавиш отново, както прави бекендът. В локален режим заявките мълчат.
//
// Черновата: `snapshot()` / `apply(snapshot)` дават и приемат въведеното
// (`StocktakeRecipesSnapshot`) — за полезния товар на черновата на листа.
import { useCallback, useMemo, useRef, useState } from "react";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  getRecipeIngredients,
  type Lookup,
  type Product,
  type RecipeIngredient,
  type Stocktake,
} from "../../../../api/inventory";
import type { CreateStocktakeRecipe } from "../../../../api/inventoryStocktakes";
import { useIsEdgeLocal } from "../../../../hooks/useEdgeMode";
import { useConfirm } from "../../components/confirm/useConfirm";
import { formatQtyUnit } from "../../components/confirm/confirmPreviews";
import {
  buildRecipeRows,
  entriesOfStocktake,
  formatPortionsInput,
  portionIngredientsOf,
  recipeQuantitiesByProduct,
  recipeRowKey,
  recipesPayloadOf,
  type RecipeIngredientsState,
  type RecipePortionIngredient,
  type StocktakeRecipeEntry,
  type StocktakeRecipeRow,
  type StocktakeRecipesSnapshot,
} from "./stocktakeRecipes";
import { parseDecimal } from "./stocktakeWorksheetShared";
import type { StocktakeRecipeSectionProps } from "./StocktakeRecipeSection";

type Params = {
  onTouch: () => void; // „незапазени промени" + чернова
  // мярката и знаците на съставките (редът „какво съдържа", прегледът при ✕)
  productById: Map<number, Product>;
  decimalsOf: (productId: number) => number;
};

// Стабилна функция: `combine` връща СЪЩИЯ обект, докато данните не се сменят
// (TanStack го сравнява дълбоко) — иначе всеки рендер би преизчислявал всички
// продуктови редове на листа
const combineIngredientStates = (
  results: UseQueryResult<RecipeIngredient[]>[],
): RecipeIngredientsState[] =>
  results.map((result): RecipeIngredientsState => {
    if (result.status === "success")
      return { status: "ready", ingredients: portionIngredientsOf(result.data) };
    // грешка — или изключена заявка (локален режим): съставките не се знаят
    if (result.status === "error" || result.fetchStatus === "idle") return { status: "error" };
    return { status: "loading" };
  });

export const useStocktakeRecipes = ({ onTouch, productById, decimalsOf }: Params) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const isLocal = useIsEdgeLocal();
  const [entries, setEntries] = useState<StocktakeRecipeEntry[]>([]);
  // Снимките от записания документ по рецепта: оцеляват премахване + ново
  // добавяне на същата рецепта (като бекенда — той ги търси по recipeId)
  const [savedByRecipe, setSavedByRecipe] = useState<Map<number, RecipePortionIngredient[]>>(
    new Map(),
  );
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // ─── Съставките на рецептите без снимка (текущата рецепта) ───────────────
  const currentRecipeIds = useMemo(
    () => entries.filter((entry) => !entry.savedIngredients).map((entry) => entry.recipeId),
    [entries],
  );
  const ingredientStates = useQueries({
    queries: currentRecipeIds.map((recipeId) => ({
      queryKey: ["inventory", "recipe-ingredients", recipeId],
      queryFn: () => getRecipeIngredients(recipeId),
      staleTime: 5 * 60 * 1000,
      enabled: !isLocal,
    })),
    combine: combineIngredientStates,
  });

  const rows = useMemo<StocktakeRecipeRow[]>(() => {
    const stateById = new Map(currentRecipeIds.map((recipeId, index) => [recipeId, ingredientStates[index]]));
    return buildRecipeRows(entries, (entry) => stateById.get(entry.recipeId) ?? { status: "loading" });
  }, [entries, currentRecipeIds, ingredientStates]);
  const inRecipesByProduct = useMemo(() => recipeQuantitiesByProduct(rows), [rows]);
  const rowKeys = useMemo(() => rows.map((row) => row.key), [rows]);
  // рецептите в листа — за маркера „✓ добавена" в търсачката и списъка
  const addedRecipeIds = useMemo<ReadonlySet<number>>(
    () => new Set(entries.map((entry) => entry.recipeId)),
    [entries],
  );
  // ... и порциите им — „Целият списък" ги показва и не презаписва без въпрос
  const portionsByRecipe = useMemo<ReadonlyMap<number, string>>(
    () => new Map(entries.map((entry) => [entry.recipeId, entry.portions])),
    [entries],
  );

  // ─── Промени ─────────────────────────────────────────────────────────────
  // Добавя (или само намира вече добавената) и връща ключа на първата — за
  // фокуса в „Порции". Новата е с празни порции: пишат се веднага. Порциите,
  // въведени в „Целия списък" (`portionsById`), отиват и във вече добавената.
  const addRecipes = (recipes: Lookup[], portionsById?: ReadonlyMap<number, string>): string | null => {
    if (!recipes.length) return null;
    const present = new Set(entriesRef.current.map((entry) => entry.recipeId));
    const portionsOf = (recipeId: number) => {
      const typed = portionsById?.get(recipeId) ?? "";
      const parsed = parseDecimal(typed);
      return typed === "" ? null : parsed > 0 ? formatPortionsInput(parsed) : typed;
    };
    const added = recipes
      .filter((recipe, index) => !present.has(recipe.id) && recipes.findIndex((r) => r.id === recipe.id) === index)
      .map(
        (recipe): StocktakeRecipeEntry => ({
          recipeId: recipe.id,
          recipeName: recipe.name,
          portions: portionsOf(recipe.id) ?? "",
          savedIngredients: savedByRecipe.get(recipe.id) ?? null,
        }),
      );
    const refilled = recipes.filter((recipe) => present.has(recipe.id) && portionsOf(recipe.id) != null);
    if (added.length || refilled.length) {
      setEntries((current) => [
        ...current.map((entry) => {
          const portions = present.has(entry.recipeId) ? portionsOf(entry.recipeId) : null;
          return portions == null ? entry : { ...entry, portions };
        }),
        ...added,
      ]);
      onTouch();
    }
    return recipeRowKey(recipes[0].id);
  };

  const removeRecipe = (recipeId: number) => {
    setEntries((current) => current.filter((entry) => entry.recipeId !== recipeId));
    onTouch();
  };

  // ✕ на реда: рецепта с порции пита (съставките ѝ излизат от преброеното)
  const requestRemove = async (recipeId: number) => {
    const row = rows.find((candidate) => candidate.entry.recipeId === recipeId);
    if (!row) return;
    if (row.portions <= 0) {
      removeRecipe(recipeId);
      return;
    }
    const confirmed = await confirm({
      intent: "warning",
      icon: <DeleteOutlined />,
      title: t("inventoryPage.cfmRemoveRowTitle", { name: row.entry.recipeName }),
      explanation: t("inventoryPage.cfmRemoveRowText", {
        // „3 порции" / „1 порция"
        qty: t("inventoryPage.cfmPortions", {
          count: row.portions,
          value: formatPortionsInput(row.portions),
        }),
      }),
      preview: row.contents.length
        ? {
            caption: t("inventoryPage.sr6RemoveCaption"),
            items: row.contents.map((content) => {
              const product = productById.get(content.productId);
              return {
                key: content.productId,
                label: content.productName || product?.name || `#${content.productId}`,
                value: formatQtyUnit(-content.quantity, product?.decimalPlaces, product?.unit, true),
              };
            }),
          }
        : undefined,
      note: { kind: "undo", text: t("inventoryPage.cfmRemoveRowUndo") },
      confirmLabel: t("inventoryPage.cfmRemoveRowConfirm"),
    });
    if (confirmed) removeRecipe(recipeId);
  };

  const patchEntry = (recipeId: number, patch: Partial<StocktakeRecipeEntry>) =>
    setEntries((current) =>
      current.map((entry) => (entry.recipeId === recipeId ? { ...entry, ...patch } : entry)),
    );

  // Порциите приемат само цифри, запетая и точка
  const onPortionsChange = (recipeId: number, rawValue: string) => {
    patchEntry(recipeId, { portions: rawValue.replace(/[^0-9.,]/g, "") });
    onTouch();
  };
  // Напускане: „2.50" → „2,5"; нула и празно остават за пречката
  const onPortionsBlur = (recipeId: number) => {
    const entry = entriesRef.current.find((candidate) => candidate.recipeId === recipeId);
    if (!entry || entry.portions === "") return;
    const parsed = parseDecimal(entry.portions);
    if (parsed > 0) patchEntry(recipeId, { portions: formatPortionsInput(parsed) });
  };

  // ─── Зареждане, чернова, запис ───────────────────────────────────────────
  // Редакция: рецептите на документа със снимките им (без „незапазени промени")
  const loadFromStocktake = useCallback((stocktake: Stocktake) => {
    const loaded = entriesOfStocktake(stocktake);
    setEntries(loaded);
    setSavedByRecipe(
      new Map(loaded.map((entry) => [entry.recipeId, entry.savedIngredients ?? []])),
    );
  }, []);

  const snapshot = useCallback(
    (): StocktakeRecipesSnapshot => ({ v: 1, entries: entriesRef.current }),
    [],
  );
  const apply = useCallback((next: StocktakeRecipesSnapshot | null | undefined) => {
    const applied = next?.v === 1 && Array.isArray(next.entries) ? next.entries : [];
    setEntries(applied);
    setSavedByRecipe(
      new Map(
        applied
          .filter((entry) => entry.savedIngredients)
          .map((entry) => [entry.recipeId, entry.savedIngredients ?? []]),
      ),
    );
  }, []);

  const payload = (): CreateStocktakeRecipe[] => recipesPayloadOf(entriesRef.current);

  // Пропсите на секцията „Приготвени рецепти" (решетка и карти)
  const section: StocktakeRecipeSectionProps = {
    rows,
    decimalsOf,
    unitOf: (productId) => productById.get(productId)?.unit ?? "",
    onPortionsChange,
    onPortionsBlur,
    onRemove: (recipeId) => void requestRemove(recipeId),
  };

  return {
    rows,
    rowKeys,
    inRecipesByProduct,
    addedRecipeIds,
    portionsByRecipe,
    addRecipes,
    section,
    loadFromStocktake,
    snapshot,
    apply,
    payload,
  };
};

export type StocktakeRecipesState = ReturnType<typeof useStocktakeRecipes>;
